import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building, Globe, Palette, ShieldCheck, Save, Download, Loader2 } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

export function SettingsTenantPage() {
  const qc = useQueryClient();
  const tenant = useQuery({
    queryKey: ['admin', 'tenant'],
    queryFn: async () => (await api.get('/api/admin/tenant')).data,
  });

  const [name, setName] = useState('');
  const [brandingColor, setBrandingColor] = useState('#1e40af');
  const [brandingLogo, setBrandingLogo] = useState('');
  const [region, setRegion] = useState<'UK' | 'EU' | 'US'>('UK');
  const [currency, setCurrency] = useState<'GBP' | 'EUR' | 'USD'>('GBP');
  const [mfaRequiredForAdmin, setMfaRequiredForAdmin] = useState(false);
  const [mfaRequiredForAll, setMfaRequiredForAll] = useState(false);

  useEffect(() => {
    if (tenant.data) {
      setName(tenant.data.name);
      setBrandingColor(tenant.data.brandingColor ?? '#1e40af');
      setBrandingLogo(tenant.data.brandingLogo ?? '');
      setRegion((tenant.data.billingRegion ?? 'UK') as 'UK' | 'EU' | 'US');
      setCurrency((tenant.data.billingCurrency ?? 'GBP') as 'GBP' | 'EUR' | 'USD');
      setMfaRequiredForAdmin(tenant.data.mfaRequiredForAdmin ?? false);
      setMfaRequiredForAll(tenant.data.mfaRequiredForAll ?? false);
    }
  }, [tenant.data]);

  const saveInfo = useMutation({
    mutationFn: async () => api.patch('/api/admin/tenant', {
      name,
      brandingColor: brandingColor || null,
      brandingLogo: brandingLogo || null,
    }),
    onSuccess: () => {
      toast.success('Tenant updated');
      qc.invalidateQueries({ queryKey: ['admin', 'tenant'] });
    },
    onError: (err) => toast.error('Update failed', extractApiError(err).message),
  });

  const saveRegion = useMutation({
    mutationFn: async () => api.patch('/api/billing/region', { region, currency }),
    onSuccess: () => {
      toast.success('Billing region updated');
      qc.invalidateQueries({ queryKey: ['admin', 'tenant'] });
    },
    onError: (err) => toast.error('Update failed', extractApiError(err).message),
  });

  if (tenant.isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Tenant settings</h1>
        <p className="text-sm text-muted-foreground">
          Organisation-level configuration. Only admins can change these.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2"><Building className="h-5 w-5" /> Organisation</span>
          </CardTitle>
          <CardDescription>Display name + branding.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Organisation name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="max-w-md" />
          </div>
          <div>
            <Label>Brand colour</Label>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-md border" style={{ background: brandingColor }} />
              <Input value={brandingColor} onChange={(e) => setBrandingColor(e.target.value)} className="max-w-[200px] font-mono" />
              <input type="color" value={brandingColor} onChange={(e) => setBrandingColor(e.target.value)} className="h-9 w-9 rounded-md border border-input cursor-pointer" />
            </div>
            <p className="mt-1 text-xs text-muted-foreground"><Palette className="inline h-3 w-3" /> Applied to the Compliance Pack PDF cover band, dashboard accent, and email headers.</p>
          </div>
          <div>
            <Label>Brand logo URL (optional)</Label>
            <Input value={brandingLogo} onChange={(e) => setBrandingLogo(e.target.value)} placeholder="https://yourcompany.com/logo.png" className="max-w-md" />
            <p className="mt-1 text-xs text-muted-foreground">When set, the Compliance Pack PDF wordmark co-brands "PRAXARA · {`{your tenant name}`}". Use a small (≤200×60 px) PNG hosted on your CDN.</p>
          </div>
          <div className="pt-2">
            <Button onClick={() => saveInfo.mutate()} disabled={saveInfo.isPending}>
              <Save className="mr-1.5 h-3.5 w-3.5" /> Save organisation
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle><span className="inline-flex items-center gap-2"><Globe className="h-5 w-5" /> Billing region</span></CardTitle>
          <CardDescription>Determines currency, VAT handling, and data residency.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div>
              <Label>Region</Label>
              <select
                value={region}
                onChange={(e) => {
                  const r = e.target.value as 'UK' | 'EU' | 'US';
                  setRegion(r);
                  setCurrency(r === 'UK' ? 'GBP' : r === 'EU' ? 'EUR' : 'USD');
                }}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="UK">United Kingdom</option>
                <option value="EU">European Union</option>
                <option value="US">United States</option>
              </select>
            </div>
            <div>
              <Label>Currency</Label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value as 'GBP' | 'EUR' | 'USD')} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="GBP">GBP (?)</option>
                <option value="EUR">EUR (?)</option>
                <option value="USD">USD ($)</option>
              </select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Data residency for {region}: {region === 'US' ? 'Azure East US (HIPAA)' : 'Azure West Europe (GDPR)'}.
          </p>
          <div>
            <Button onClick={() => saveRegion.mutate()} disabled={saveRegion.isPending}>Save region</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle><span className="inline-flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Security policy</span></CardTitle>
          <CardDescription>Enforcement of MFA and other account controls.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Toggle
            label="Require MFA for all ADMIN users"
            description="Users with ADMIN role must enable MFA before they can sign in."
            checked={mfaRequiredForAdmin}
            onChange={setMfaRequiredForAdmin}
          />
          <Toggle
            label="Require MFA for everyone"
            description="All users — reviewers, operators, viewers — must enable MFA."
            checked={mfaRequiredForAll}
            onChange={setMfaRequiredForAll}
          />
          <p className="text-xs text-muted-foreground">
            (Admin MFA enforcement settings will save on next API release.)
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle><span className="inline-flex items-center gap-2"><Download className="h-5 w-5" /> Tenant data export</span></CardTitle>
          <CardDescription>
            Download every record Praxara holds for your organisation, in JSON.
            Satisfies GDPR Article 20 (right to data portability).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
            <li>One JSON file per resource (users, documents metadata, audit log, extractions, workflows, packs, ROPA, DSRs, ...).</li>
            <li>MANIFEST.json records SHA-256 of every file for integrity verification.</li>
            <li>Sensitive fields stripped: password hashes, MFA secrets, API key hashes.</li>
            <li>Document file blobs are not bundled (download per-document via the API).</li>
            <li>Audit log capped at the most-recent 50,000 entries; total count noted in the file.</li>
          </ul>
          <ExportTenantButton tenantSlug={tenant.data.slug} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tenant details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Row label="Tenant ID" value={<code className="text-xs">{tenant.data.id}</code>} />
          <Row label="Slug" value={<code className="text-xs">{tenant.data.slug}</code>} />
          <Row label="Plan" value={<Badge>{tenant.data.plan}</Badge>} />
          <Row label="Max seats" value={tenant.data.maxSeats} />
          <Row label="GxP validated" value={tenant.data.gxpValidated ? <Badge variant="success">Yes</Badge> : <Badge variant="secondary">Pending</Badge>} />
          <Row label="Created" value={new Date(tenant.data.createdAt).toLocaleDateString()} />
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function ExportTenantButton({ tenantSlug }: { tenantSlug: string }) {
  const [busy, setBusy] = useState(false);
  const handleExport = async () => {
    setBusy(true);
    try {
      const res = await api.post('/api/gdpr/tenant-export', undefined, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `praxara-tenant-export-${tenantSlug}-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded', 'Verify file SHA-256 against MANIFEST.json.');
    } catch (err) {
      toast.error('Export failed', extractApiError(err).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button onClick={handleExport} disabled={busy}>
      {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}
      {busy ? 'Building zip...' : 'Download tenant export (.zip)'}
    </Button>
  );
}

function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between rounded-md border p-3 hover:bg-accent/50">
      <div className="flex-1 pr-4">
        <div className="font-medium text-sm">{label}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-primary"
      />
    </label>
  );
}
